import {
  getCustomOidcEnabled,
  getCustomOidcSettings,
  getUserManager,
} from "@p0security/frontend-core";
import { PropsWithChildren, useCallback, useMemo } from "react";
import { AuthProvider } from "react-oidc-context";

/**
 * Provider for custom OIDC authentication using react-oidc-context.
 * This provider wraps the entire app and is controlled by config.auth from runtime configuration.
 */

const EnabledCustomOidcProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const settings = useMemo(() => getCustomOidcSettings(), []);
  const userManager = useMemo(() => getUserManager(), []);

  const onSigninCallback = useCallback(() => {
    // Clean URL after react-oidc-context processes callback
    // This must happen here to ensure proper state validation timing
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  const matchSignoutCallback = useCallback(() => {
    if (
      !("post_logout_redirect_uri" in settings) ||
      !settings.post_logout_redirect_uri
    ) {
      return false;
    }

    try {
      // Normalize to string
      const postLogoutUrl =
        typeof settings.post_logout_redirect_uri === "string"
          ? new URL(settings.post_logout_redirect_uri, window.location.origin)
          : settings.post_logout_redirect_uri;

      return window.location.href.startsWith(postLogoutUrl.href);
    } catch {
      return false;
    }
  }, [settings]);

  return (
    <AuthProvider
      userManager={userManager ?? undefined}
      onSigninCallback={onSigninCallback}
      matchSignoutCallback={matchSignoutCallback}
    >
      {children}
    </AuthProvider>
  );
};

export const CustomOidcProvider: React.FC<PropsWithChildren> = (props) => {
  const isEnabled = useMemo(() => getCustomOidcEnabled(), []);
  if (isEnabled) return <EnabledCustomOidcProvider {...props} />;

  return (
    <AuthProvider
      // Minimal valid settings required by AuthProvider when OIDC is disabled
      authority=""
      client_id="disabled" //disabled.local"
      redirect_uri={window.location.origin}
      response_type={"code"}
      automaticSilentRenew={false}
    >
      {props.children}
    </AuthProvider>
  );
};
